import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { subscribeRealtime } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import ConversationList from "@/components/inbox/ConversationList";
import ChatPane from "@/components/inbox/ChatPane";
import ContextPanel from "@/components/inbox/ContextPanel";

export default function Inbox() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [agents, setAgents] = useState([]);
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations", { params: { filter, search, agent: agentFilter } });
      setConvs(data);
      if (selectedRef.current) {
        const upd = data.find((c) => c.id === selectedRef.current.id);
        if (upd) setSelected((s) => ({ ...s, ...upd }));
      }
    } catch (e) {}
  }, [filter, search, agentFilter]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const loadUsers = () => api.get("/users").then(({ data }) => setAgents(data.filter((u) => u.active !== false))).catch(() => {});
    loadUsers();
    const unsub = subscribeRealtime((ev) => {
      if (ev.type === "presence") loadUsers();
    });
    const t = setInterval(loadUsers, 60000);
    return () => { clearInterval(t); unsub(); };
  }, [user]);

  useEffect(() => {
    load();
    const unsub = subscribeRealtime((ev) => {
      if (ev.type === "message" || ev.type === "conversations") load();
    });
    const t = setInterval(load, 15000);
    return () => { clearInterval(t); unsub(); };
  }, [load]);

  const startChat = async (phone, name) => {
    try {
      const { data } = await api.post("/conversations/start", { phone, name });
      await load();
      setSelected(data);
      toast.success("Conversation ready");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="h-full flex">
      <ConversationList
        convs={convs}
        selected={selected}
        onSelect={setSelected}
        filter={filter}
        setFilter={setFilter}
        search={search}
        setSearch={setSearch}
        onStartChat={startChat}
        isAdmin={user?.role === "admin"}
        agents={agents}
        agentFilter={agentFilter}
        setAgentFilter={setAgentFilter}
      />
      {selected ? (
        <>
          <ChatPane key={selected.id} conv={selected} onConvUpdate={load} />
          <ContextPanel key={`ctx-${selected.id}`} conv={selected} />
        </>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 chat-bg">
          <div className="card-tactile card-yellow px-8 py-6 rotate-[-1deg]">
            <div className="font-heading font-bold text-xl">Shared WhatsApp Inbox</div>
            <div className="font-hand text-lg text-amber-800">pick a conversation to start chatting ✦</div>
          </div>
        </div>
      )}
    </div>
  );
}
